// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Deterministic fit solver for the shipping label (D-SHIP-11).
 *
 * The label is a fixed-size print target; its content is scaled uniformly
 * (with the pre-transform width widened by 1/scale so the scaled result still
 * spans the full label) so that it fills the label without spilling or being
 * cut off. Because changing the scale changes the layout width — which re-wraps
 * text and resizes the full-width barcode — the rendered height is a function
 * of the scale that can only be probed, not computed. `renderedAt(s)` applies
 * a candidate scale to the live DOM and returns the resulting rendered height
 * (layout height × s).
 *
 * renderedAt is monotonically non-decreasing in s (a narrower pre-transform
 * width never makes content shorter), so a bounded binary search finds the
 * largest scale whose rendered height still fits `target`. Every probe happens
 * synchronously inside one layout pass — no React state feedback loop, so it
 * cannot oscillate or hit "maximum update depth" (the failure mode of the
 * previous implementation).
 */

export interface FitBounds { min: number; max: number }

/** Grow cap: sparse labels are enlarged to fill, but not into billboard text. */
export const LABEL_FIT_BOUNDS: FitBounds = { min: 0.3, max: 1.35 }

/**
 * Returns the largest scale in [bounds.min, bounds.max] such that
 * renderedAt(scale) <= target. If even bounds.min overflows, returns bounds.min
 * (best effort — the one-label guarantee beats a readability floor). The caller
 * must re-apply the returned scale (the last probe may not be the answer).
 */
export function solveLabelScale(
  renderedAt: (s: number) => number,
  target: number,
  bounds: FitBounds = LABEL_FIT_BOUNDS,
): number {
  let lo = bounds.min
  let hi = bounds.max
  if (renderedAt(hi) <= target) return hi
  if (renderedAt(lo) > target) return lo
  // Invariant: renderedAt(lo) <= target < renderedAt(hi). 10 halvings on a
  // ~1.0 interval give ~0.001 precision — below a device pixel at label size.
  for (let i = 0; i < 10; i++) {
    const mid = (lo + hi) / 2
    if (renderedAt(mid) <= target) lo = mid
    else hi = mid
  }
  return lo
}
